import { useCallback } from 'react';
import { format } from 'date-fns';
import { useTranslation } from '@/i18n';
import { toHotelPensionType } from '@/constants/hotelPension';
import type { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import { getHotelRoomRepartitionLabel } from '@/utils/benefits/getHotelRoomRepartitionLabel';
import { ROOM_COMPOSITIONS } from '@/utils/benefits/hotel/excelPaste/roomCompositions';
import type { ExcelPasteIssue } from '@/utils/benefits/hotel/excelPaste/types';

type ChildAges =
  | Array<{ childAgeFrom?: number; childAgeTo?: number }>
  | undefined;

/** Short codes used in the grids and the brief, the same in every language. */
const PENSION_CODES: Record<HotelPensionType, string> = {
  SingleRoom: 'RO',
  Breakfast: 'BB',
  SemiPension: 'HB',
  FullPension: 'FB',
  AllInclusive: 'AI',
};

export const formatPastedAmount = (value: unknown) =>
  value === null || value === undefined || value === ''
    ? ''
    : String(value).replace('.', ',');

export const formatPastedDate = (value: unknown) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? '' : format(date, 'dd/MM/yy');
};

/** Labels and messages shared by the three paste flows. */
export function useExcelPasteFormatters(childAges: ChildAges) {
  const { t } = useTranslation();

  const formatComposition = useCallback(
    (row: number) => {
      const composition = ROOM_COMPOSITIONS[row - 1];
      if (!composition) return String(row);
      const { roomType, adults, younger, older } = composition;
      return `${t(`hotels.roomTarifs.${roomType}`)} ${getHotelRoomRepartitionLabel(
        [adults, younger, older],
        childAges ?? [],
      )}`;
    },
    [childAges, t],
  );

  const formatPensionType = useCallback(
    (value: string | number | undefined) => {
      const pensionType = toHotelPensionType(String(value ?? ''));
      return pensionType ? PENSION_CODES[pensionType] : String(value ?? '');
    },
    [],
  );

  const formatIssue = useCallback(
    ({ code, params = {} }: ExcelPasteIssue) => {
      const values: Record<string, string | number> = { ...params };
      if (code === 'compositionWithoutPrice') {
        values.compositions = String(params.compositions)
          .split(',')
          .map((row) => formatComposition(Number(row)))
          .join(', ');
      }
      if (code === 'priceDecreases') {
        values.larger = formatComposition(Number(params.larger));
        values.smaller = formatComposition(Number(params.smaller));
      }
      if (
        code === 'pensionNotAboveBase' ||
        code === 'supplementPensionNotOffered'
      ) {
        values.base = formatPensionType(params.base);
        values.pensionType = formatPensionType(params.pensionType);
      }
      if (code === 'missingChildBand') {
        values.band = t(`hotels.pasteFromExcel.bands.${params.band}`);
      }
      const message = t(`hotels.pasteFromExcel.issues.${code}`, {
        ...values,
        interpolation: { escapeValue: false },
      });
      // Whole-category paste: say which tariff of the block the issue is about.
      if (params.inTariff !== undefined) {
        return t('hotels.pasteFromExcel.inTariff', {
          tariff: params.inTariff,
          message,
          interpolation: { escapeValue: false },
        });
      }
      if (params.inColumn !== undefined) {
        return t('hotels.pasteFromExcel.inColumn', {
          index: params.inColumn,
          message,
          interpolation: { escapeValue: false },
        });
      }
      return message;
    },
    [formatComposition, formatPensionType, t],
  );

  /** "BB, HB, FB" for a list of pension types, in the grids' order. */
  const formatPensionCodes = useCallback(
    (pensionTypes: string[]) =>
      (Object.keys(PENSION_CODES) as HotelPensionType[])
        .filter((pensionType) => pensionTypes.includes(pensionType))
        .map((pensionType) => PENSION_CODES[pensionType])
        .join(', '),
    [],
  );

  return { formatIssue, formatComposition, formatPensionCodes };
}
